import { signal } from '@preact/signals';

import { isNativeApp } from '@amumin/platform/capacitor';

import { api } from '../api.js';

// Meldingen op je telefoon in de browser (0038_meldingen; docs/DESIGN.md §8.PU): de service worker
// (public/sw.js, scope /), de installatieprompt van Chrome/Edge en het aanmelden bij de pushdienst
// van de browser. Alleen de ouderapp; de kiosk kent dit niet.

export type PushPlatform = 'ios' | 'android' | 'desktop';

export type PushSupport =
  /** Kan meldingen krijgen (service worker + PushManager + Notification). */
  | 'ok'
  /** iPhone/iPad in Safari zonder beginscherm: eerst installeren. */
  | 'needs_install'
  /** In de iPhone-app (apps/mobile): een WKWebView kent geen Web Push; APNs komt met route 2. */
  | 'native_app'
  | 'unsupported';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** De bewaarde installatieprompt (Chrome/Edge op Android en de computer). */
export const installPrompt = signal<BeforeInstallPromptEvent | null>(null);
/** Net geïnstalleerd (appinstalled). */
export const justInstalled = signal(false);

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

/** Het toestel voor de stappen (alleen voor de uitleg; nooit naar de server). */
export function detectPlatform(ua = navigator.userAgent): PushPlatform {
  const iPadOs =
    /Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPadOs) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/** Draait de app vanaf het beginscherm (standalone)? */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
}

export function pushSupport(): PushSupport {
  if (isNativeApp()) return 'native_app';
  const has =
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window;
  if (has && window.isSecureContext) return 'ok';
  if (detectPlatform() === 'ios' && !isStandalone()) return 'needs_install';
  return 'unsupported';
}

/** Registreert de service worker (één keer); null als de browser dat niet kan. */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return registration;
  if (!('serviceWorker' in navigator) || !window.isSecureContext) {
    registration = Promise.resolve(null);
    return registration;
  }
  registration = navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then(() => navigator.serviceWorker.ready)
    .catch(() => null);
  return registration;
}

/** Vroeg in main.tsx: service worker en de installatieprompt opvangen. */
export function initPwa(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installPrompt.value = e as BeforeInstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    installPrompt.value = null;
    justInstalled.value = true;
  });
  void registerServiceWorker();
}

/** Laat de installatieprompt van de browser zien (Chrome/Edge). */
export async function promptInstall(): Promise<boolean> {
  const ev = installPrompt.value;
  if (!ev) return false;
  await ev.prompt();
  const choice = await ev.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
  installPrompt.value = null;
  return choice.outcome === 'accepted';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** De huidige aanmelding van deze browser (of null). */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'ok') return null;
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription().catch(() => null);
}

export type EnableResult = 'ok' | 'denied' | 'unsupported';

/**
 * Toestemming vragen (alleen na een tik), aanmelden bij de pushdienst van de browser met de
 * publieke sleutel van de server, en de aanmelding aan de server geven.
 */
export async function enablePush(vapidPublicKey: string): Promise<EnableResult> {
  if (pushSupport() !== 'ok') return 'unsupported';
  const permission =
    Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;
  if (permission !== 'granted') return 'denied';
  const reg = await registerServiceWorker();
  if (!reg) return 'unsupported';
  let sub = await reg.pushManager.getSubscription();
  const wanted = keyBytes(vapidPublicKey);
  if (sub) {
    // Aangemeld met een andere sleutel van de server (sleutelwissel): eerst afmelden.
    const had = sub.options.applicationServerKey;
    const same =
      had !== null &&
      had !== undefined &&
      new Uint8Array(had).every((b, i) => b === wanted[i]) &&
      had.byteLength === wanted.byteLength;
    if (!same) {
      await sub.unsubscribe().catch(() => false);
      sub = null;
    }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: wanted });
  const json = sub.toJSON();
  await api.push.subscribe({
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    expirationTime: sub.expirationTime ?? null,
  });
  return 'ok';
}

/** Deze browser afmelden (bij de server én bij de pushdienst). Fouten zijn niet erg. */
export async function disablePush(): Promise<void> {
  forgetPushOptIn();
  const sub = await currentSubscription();
  if (!sub) return;
  await api.push.unsubscribe(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe().catch(() => false);
}

/** Bij uitloggen: deze browser krijgt geen meldingen meer voor deze login. */
export async function forgetPushOnLogout(): Promise<void> {
  // Hooguit 3 seconden: uitloggen wacht nooit lang op de browser of de pushdienst.
  const work = (async () => {
    const sub = await currentSubscription();
    if (!sub) return;
    await api.push.unsubscribe(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => false);
  })().catch(() => undefined);
  await Promise.race([work, new Promise<void>((resolve) => setTimeout(resolve, 3000))]);
}

// ---- Na het inloggen (nieuwe adressen, 28 sep 2026) --------------------------------------------
// Toestemming voor meldingen hoort bij een ADRES (origin). Op amumin.app heeft de browser nog geen
// toestemming, ook al stonden meldingen op f.amumin.com aan. Na het inloggen:
//  - toestemming al gegeven én deze browser had meldingen aan voor deze persoon (merkje in de
//    lokale opslag, gezet bij aanzetten): stil opnieuw aanmelden bij de pushdienst en de server;
//  - anders, als deze persoon elders meldingen heeft, of ze vóór de verhuizing aan had (de server
//    trok bij de verhuizing alle oude sessies in, en daarmee hun aanmeldingen: resume_after_move),
//    en de browser nog niets vroeg: een rustige vraag (PushResumeBanner), die "Niet nu" onthoudt.
// Bij de verhuizing zelf is er dus altijd die ene vraag: toestemming en het merkje horen bij het
// oude adres. Het stille pad helpt pas bij een latere nieuwe login op amumin.app.
// Nooit zelf om toestemming vragen zonder tik (browsers blokkeren dat, en het is onrustig).

const OPT_IN_KEY = 'fb.push_optin';
const RESUME_DISMISSED_KEY = 'fb.push_resume_later';

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Geen opslag (privévenster): dan vragen we het na een volgende login gewoon opnieuw.
  }
}

/** Deze browser heeft meldingen aangezet voor deze persoon (na een geslaagde enablePush). */
export function rememberPushOptIn(identityId: string): void {
  writeLocal(OPT_IN_KEY, identityId);
}

/** Meldingen bewust uitgezet in deze browser: niet meer vanzelf aanmelden. */
export function forgetPushOptIn(): void {
  writeLocal(OPT_IN_KEY, null);
}

export function dismissPushResume(identityId: string): void {
  writeLocal(RESUME_DISMISSED_KEY, identityId);
}

export interface ResumeInput {
  support: PushSupport;
  permission: NotificationPermission | 'unsupported';
  available: boolean;
  allowed: boolean;
  hasKey: boolean;
  /** Aanmeldingen van deze persoon op de server. */
  subscriptions: ReadonlyArray<{ this_device: boolean }>;
  /** Had meldingen aan vóór de verhuizing naar amumin.app (server: resume_after_move). */
  hadBeforeMove?: boolean;
  /** Merkje in deze browser voor deze persoon. */
  optedIn: boolean;
  /** "Niet nu" eerder gekozen door deze persoon in deze browser. */
  dismissed: boolean;
}

export type ResumeDecision = 'none' | 'resubscribe' | 'ask';

/** Puur: wat doen we na het inloggen met meldingen in deze browser? */
export function decideResume(i: ResumeInput): ResumeDecision {
  if (i.support !== 'ok' || !i.available || !i.allowed || !i.hasKey) return 'none';
  if (i.subscriptions.some((s) => s.this_device)) return 'none';
  if (i.permission === 'granted' && i.optedIn) return 'resubscribe';
  if (i.permission === 'denied' || i.permission === 'unsupported') return 'none';
  if (i.dismissed || (i.subscriptions.length === 0 && !i.hadBeforeMove)) return 'none';
  return 'ask';
}

/** Na het inloggen: stil opnieuw aanmelden, of 'ask' voor de rustige vraag. */
export async function resumePushAfterLogin(identityId: string): Promise<ResumeDecision> {
  const support = pushSupport();
  if (support !== 'ok') return 'none';
  const settings = await api.push.settings().catch(() => null);
  if (!settings) return 'none';
  const decision = decideResume({
    support,
    permission: typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
    available: settings.available,
    allowed: settings.allowed,
    hasKey: !!settings.vapid_public_key,
    subscriptions: settings.subscriptions,
    hadBeforeMove: settings.resume_after_move === true,
    optedIn: readLocal(OPT_IN_KEY) === identityId,
    dismissed: readLocal(RESUME_DISMISSED_KEY) === identityId,
  });
  if (decision !== 'resubscribe' || !settings.vapid_public_key) return decision;
  const out = await enablePush(settings.vapid_public_key).catch(() => 'unsupported' as const);
  return out === 'ok' ? 'resubscribe' : 'none';
}
